import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAppNavigationGuard } from "../contexts/AppNavigationGuardContext";
import type { HomeActivityLog } from "../lib/reportActivity";
import { createHomeReportFingerprint } from "../lib/homeReports";

type HomeReportDrawerProps = {
  characterLastName: string;
  todayJobLogs: HomeActivityLog[];
  yesterdayJobLogs: HomeActivityLog[];
  newReportFingerprints: string[];
  onLogsViewed: (logs: HomeActivityLog[]) => void;
  onClose: () => void;
  onNavigate?: () => void;
};

type ReportSectionProps = {
  title: string;
  logs: HomeActivityLog[];
  emphasize?: boolean;
};

function ReportSection({ title, logs, emphasize = false, onNavigate }: ReportSectionProps & { onNavigate?: React.MouseEventHandler<HTMLAnchorElement> }) {
  if (logs.length === 0) return null;

  return (
    <section aria-label={title}>
      <div className="mb-2 flex items-center gap-2">
        <h3 className={`text-xs font-black tracking-[0.08em] ${emphasize ? "text-[#B65A4D]" : "text-[#A67C52]"}`}>
          {title}
        </h3>
        <div className="h-px flex-1 bg-[#EADBC8]" />
      </div>
      <ul className="space-y-2">
        {logs.map((log) => {
          const content = (
            <>
              <span
                className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                  log.isActive ? "animate-pulse bg-[#D39A45]" : emphasize ? "bg-[#B65A4D]" : "bg-[#C9B4A9]"
                }`}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 whitespace-pre-line text-sm font-bold leading-6 text-[#5D4D46]">
                {log.text}
              </span>
              <span className="shrink-0 pt-1 text-[11px] font-black text-[#A67C52]">{log.time}</span>
            </>
          );

          return (
            <li key={`${log.id}-${createHomeReportFingerprint(log)}`}>
              {log.to ? (
                <Link
                  to={log.to}
                  onClick={onNavigate}
                  className="flex items-start gap-3 rounded-[1rem] px-2 py-2 transition hover:bg-[#FFF8F1]"
                >
                  {content}
                </Link>
              ) : (
                <div className="flex items-start gap-3 rounded-[1rem] px-2 py-2">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function HomeReportDrawer({
  characterLastName,
  todayJobLogs,
  yesterdayJobLogs,
  newReportFingerprints,
  onLogsViewed,
  onClose,
  onNavigate,
}: HomeReportDrawerProps) {
  const { canNavigate } = useAppNavigationGuard();
  const handleNavigate = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((event) => {
    if (!canNavigate()) {
      event.preventDefault();
      return;
    }
    onNavigate?.();
  }, [canNavigate, onNavigate]);
  const newReports = useMemo(() => new Set(newReportFingerprints), [newReportFingerprints]);
  const hasTodayUnseenResolved = todayJobLogs.some(
    (log) => log.isActive !== true && newReports.has(createHomeReportFingerprint(log))
  );
  const hasYesterdayUnseenResolved = yesterdayJobLogs.some(
    (log) => log.isActive !== true && newReports.has(createHomeReportFingerprint(log))
  );
  const [showYesterday, setShowYesterday] = useState(
    () => !hasTodayUnseenResolved && hasYesterdayUnseenResolved
  );
  const [expanded, setExpanded] = useState(false);
  const [viewedReportFingerprints, setViewedReportFingerprints] = useState<string[]>([]);
  const pageSize = 4;
  const displayedLogs = useMemo(
    () => (showYesterday ? yesterdayJobLogs : todayJobLogs),
    [showYesterday, todayJobLogs, yesterdayJobLogs]
  );
  const visibleLogs = useMemo(
    () => (expanded ? displayedLogs : displayedLogs.slice(0, pageSize)),
    [displayedLogs, expanded]
  );
  const hiddenCount = Math.max(0, displayedLogs.length - pageSize);
  const viewedReports = useMemo(() => new Set(viewedReportFingerprints), [viewedReportFingerprints]);
  const isUnseenResolved = (log: HomeActivityLog) =>
    log.isActive !== true && newReports.has(createHomeReportFingerprint(log));
  const isUnviewedNewReport = (log: HomeActivityLog) => {
    const fingerprint = createHomeReportFingerprint(log);
    return isUnseenResolved(log) && !viewedReports.has(fingerprint);
  };
  const hiddenUnseenCount = displayedLogs.slice(pageSize).filter(isUnviewedNewReport).length;
  const todayUnseenCount = todayJobLogs.filter(isUnviewedNewReport).length;
  const yesterdayUnseenCount = yesterdayJobLogs.filter(isUnviewedNewReport).length;
  const activeLogs = visibleLogs.filter((log) => log.isActive === true);
  const unseenLogs = visibleLogs.filter(isUnseenResolved);
  const seenLogs = visibleLogs.filter(
    (log) => log.isActive !== true && !newReports.has(createHomeReportFingerprint(log))
  );

  useEffect(() => {
    if (visibleLogs.length > 0) {
      const viewed = visibleLogs
        .filter((log) => log.isActive !== true)
        .map(createHomeReportFingerprint);
      setViewedReportFingerprints((current) => {
        const next = Array.from(new Set([...current, ...viewed]));
        return next.length === current.length ? current : next;
      });
      onLogsViewed(visibleLogs);
    }
  }, [onLogsViewed, visibleLogs]);

  return (
    <>
      <button
        type="button"
        aria-label="報告を閉じる"
        className="fixed inset-0 z-40 cursor-default bg-[#4A3E39]/25 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-report-drawer-title"
        className="fixed inset-x-0 bottom-0 z-50 max-h-[82vh] overflow-y-auto rounded-t-[2rem] border border-[#EADBC8] bg-[#FFFCFA] px-5 pb-7 pt-5 shadow-[0_-18px_50px_rgba(74,62,57,0.20)] md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[26rem] md:rounded-l-[2rem] md:rounded-tr-none md:px-6 md:pt-7"
      >
        <header className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-black tracking-[0.12em] text-[#A67C52]">REPORTS</p>
            <h2 id="home-report-drawer-title" className="mt-1 text-xl font-black text-[#5D4D46]">
              {characterLastName}からの報告
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EADBC8] bg-white text-lg font-black text-[#7C5A4E]"
            aria-label="閉じる"
          >
            ×
          </button>
        </header>

        <div className="mt-5 flex justify-end">
          {yesterdayJobLogs.length > 0 ? (
            <button
              type="button"
              onClick={() => {
                setShowYesterday((current) => !current);
                setExpanded(false);
              }}
              className="text-xs font-black text-[#A67C52] underline"
            >
              {showYesterday
                ? `今日を見る${todayUnseenCount > 0 ? `（新着${todayUnseenCount}件）` : ""}`
                : `前日を見る${yesterdayUnseenCount > 0 ? `（新着${yesterdayUnseenCount}件）` : ""}`}
            </button>
          ) : null}
        </div>

        <div className="mt-4 space-y-6">
          <ReportSection title="処理中" logs={activeLogs} onNavigate={handleNavigate} />
          <ReportSection title="新しい報告" logs={unseenLogs} emphasize onNavigate={handleNavigate} />
          <ReportSection title={showYesterday ? "前日" : "今日"} logs={seenLogs} onNavigate={handleNavigate} />

          {displayedLogs.length === 0 ? (
            <p className="rounded-[1rem] bg-[#FFF8F1] px-4 py-4 text-sm font-bold text-[#7B6861]">
              まだ報告はありません。
            </p>
          ) : null}

          {!expanded && hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs font-black text-[#A67C52] underline"
            >
              もっと見る（{hiddenCount}件{hiddenUnseenCount > 0 ? `・新着${hiddenUnseenCount}件` : ""}）
            </button>
          ) : null}
        </div>
      </aside>
    </>
  );
}
